import { BoxProps } from '@apia/theme';
import { useEffect, useState } from 'react';
import AutocompleteStore from './store';
import { TId } from './types';
import { addBoundary } from '../../../../utils/number';

export default class AutocompleteKeyhandler extends AutocompleteStore {
  getEvents(): BoxProps {
    return {};
  }

  end() {
    this.updateFocused((index, options) => {
      for (let i = options.length - 1; i >= 1; i--) {
        if (!options[i].isHidden) return i;
      }
      return index;
    });
  }

  home() {
    this.updateFocused((_, options) => {
      return options.findIndex((current) => !current.isHidden);
    });
  }

  pageDown() {
    this.updateFocused((index, options) => {
      const mappedOptions = options
        .map((current, i) => ({ ...current, i }))
        .filter((current) => !current.isHidden);
      const newIndex =
        mappedOptions[addBoundary(index + 10, 0, mappedOptions.length - 1)].i;

      return newIndex;
    }, false);
  }

  pageUp() {
    this.updateFocused((index, options) => {
      const mappedOptions = options
        .map((current, i) => ({ ...current, i }))
        .filter((current) => !current.isHidden);
      const newIndex =
        mappedOptions[addBoundary(index - 10, 0, mappedOptions.length - 1)].i;

      return newIndex;
    }, false);
  }

  arrowDown() {
    this.updateFocused((index, options) => {
      for (let i = index + 1; i < options.length; i++) {
        if (!options[i].isHidden) return i;
      }
      return index;
    }, false);
  }

  arrowUp() {
    this.updateFocused((index, options) => {
      for (let i = index - 1; i >= 0; i--) {
        if (!options[i].isHidden) return i;
      }
      return index;
    }, false);
  }

  useFocusedOption = () => {
    const [focusedOption, setFocusedOption] = useState(this.getFocusedOption());

    useEffect(() => {
      setFocusedOption(this.getFocusedOption());
      return this.on('focusChange', (newIndex) =>
        setFocusedOption(this.options[newIndex]),
      );
    }, []);

    return focusedOption;
  };

  useIsFocused = (id: number) => {
    const [isFocused, setIsFocused] = useState(this.isFocused(id));

    useEffect(() => {
      setIsFocused(this.isFocused(id));
      return this.onOptionChangeFocus(id, setIsFocused);
    }, [id]);

    return isFocused;
  };

  useList = () => {
    const [options, setOptions] = useState<typeof this.options>(this.options);

    useEffect(() => {
      setOptions(this.options);
      return this.on('listChange', (newOptions) => {
        setOptions(newOptions);
      });
    }, []);

    return options;
  };

  useIsLoading = () => {
    const [isLoading, setIsLoading] = useState<boolean>(this.isLoading);

    useEffect(() => {
      setIsLoading(this.isLoading);
      return this.on('isLoading', (newIsLoading) => {
        setIsLoading(newIsLoading);
      });
    }, []);

    return isLoading;
  };

  useShowValue = () => {
    const [value, setValue] = useState<string>(
      typeof this.showValue === 'string'
        ? this.showValue
        : this.showValue?.label ?? '',
    );

    useEffect(() => {
      return this.on('showValueChange', (newShowValue) => {
        setValue(
          typeof newShowValue === 'string'
            ? newShowValue
            : newShowValue?.label ?? '',
        );
      });
    }, []);

    return value;
  };

  useValue = (cb?: (newValue: TId) => unknown) => {
    const [value, setValue] = useState<TId>(
      this.value ?? this.options[0]?.value ?? '',
    );

    useEffect(() => {
      return this.on('valueChange', (newValue) => {
        const actualNewValue = newValue?.value ?? this.options[0]?.value ?? '';
        cb?.(actualNewValue);
        setValue(actualNewValue);
      });
    }, [cb]);

    return value;
  };
}
